import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState(""); // For 2FA code
  const [error, setError] = useState("");
  const [show2FAModal, setShow2FAModal] = useState(false); // Toggle for 2FA modal

  // Resend verification modal state
  const [showResend, setShowResend] = useState(false);
  const [resendMsg, setResendMsg] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const resendIntervalRef = useRef(null);

  // Add keyframes for animations
  const keyframes = `
    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
    @keyframes slideUp {
        from { transform: translate(-50%, -60%); opacity: 0; }
        to { transform: translate(-50%, -50%); opacity: 1; }
    }
  `;

  // Add keyframes to a <style> tag in the DOM
  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.innerHTML = keyframes;
    document.head.appendChild(styleTag);

    return () => {
      document.head.removeChild(styleTag);
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
        resendIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResendVerification = async () => {
    try {
      setResendLoading(true);
      setResendMsg("");
      setCooldown(0);

      await axios.post("/api/auth/resend-verification", { username });

      setResendMsg("Verification email sent. Please check your inbox.");
    } catch (e) {
      const msg =
        e.response?.data?.msg ||
        "Could not resend verification email. Try again shortly.";
      setResendMsg(msg);

      // If the backend sent a cooldown message with seconds in it
      const match = msg.match(/wait (\d+) seconds/i);
      if (match && match[1]) {
        let seconds = parseInt(match[1], 10);
        setCooldown(seconds);

        // Start countdown (clear any previous)
        if (resendIntervalRef.current) {
          clearInterval(resendIntervalRef.current);
          resendIntervalRef.current = null;
        }
        resendIntervalRef.current = setInterval(() => {
          seconds -= 1;
          if (seconds <= 0) {
            clearInterval(resendIntervalRef.current);
            resendIntervalRef.current = null;
            setCooldown(0);
          } else {
            setCooldown(seconds);
          }
        }, 1000);
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      const res = await axios.post("/api/auth/login", {
        username,
        password,
        code: verificationCode || undefined, // Include 2FA code if entered
      });

      const { accessToken, refreshToken } = res.data;

      // Save tokens to localStorage
      localStorage.setItem("token", accessToken);
      localStorage.setItem("refreshToken", refreshToken);

      // Redirect the user after successful login
      const redirectTo = localStorage.getItem("redirectAfterLogin") || "/";
      console.log("Redirecting to:", redirectTo);

      // Clean up the redirectAfterLogin
      localStorage.removeItem("redirectAfterLogin");

      // Reload the page or navigate to the intended URL
      window.location.href = redirectTo;
    } catch (err) {
      const errorMessage = err.response?.data?.msg;

      if (errorMessage === "2FA code is required.") {
        setShow2FAModal(true);
        setError("Enter your 2FA code.");
      } else if (errorMessage === "Invalid 2FA code.") {
        setError("Invalid 2FA code. Please try again.");
      } else if (errorMessage === "Please verify your email before logging in.") {
        setError(errorMessage);
        setShowResend(true);
      } else {
        setError(errorMessage || "Invalid username or password.");
      }
    }
  };

  const handle2FASubmit = async () => {
    await handleLogin(); // Retry login with the 2FA code
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      show2FAModal ? handle2FASubmit() : handleLogin();
    }
  };

  // Automatically focus on the 2FA input when the modal appears
  useEffect(() => {
    if (show2FAModal) {
      const input = document.getElementById("2fa-input");
      if (input) input.focus();
    }
  }, [show2FAModal]);

  return (
    <div style={styles.container}>
      <Helmet>
        <title>Login - Sintrix.net</title>
        <meta
          name="description"
          content="Login to access your account on Sintrix.net. Enter your username and password to get started."
        />
      </Helmet>

      <div style={styles.formContainer}>
        <h2 style={styles.title}>Login</h2>
        {error && <p style={styles.errorMessage}>{error}</p>}

        <input
          type="text"
          placeholder="Username or Email"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onKeyDown={handleKeyDown}
          style={styles.input}
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
          style={styles.input}
        />
        <button onClick={handleLogin} style={styles.button}>
          Login
        </button>

        <p style={styles.linkText}>
          Forgot password?{" "}
          <Link to="/forgot-password" style={styles.link}>
            Reset it here
          </Link>
        </p>
        <p style={styles.linkText}>
          Don't have an account?{" "}
          <Link to="/signup" style={styles.link}>
            Sign up here
          </Link>
        </p>
      </div>

      {/* Resend Verification Modal */}
      {showResend && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Verify Your Email</h3>
            <p style={styles.modalDescription}>
              Your account isn’t verified yet. Click the button below to resend
              the verification email.
            </p>

            <button
              onClick={handleResendVerification}
              disabled={resendLoading || !username || cooldown > 0}
              style={{
                ...styles.button,
                opacity: resendLoading || cooldown > 0 ? 0.6 : 1,
              }}
            >
              {resendLoading
                ? "Sending..."
                : cooldown > 0
                ? `Resend available in ${cooldown}s`
                : "Resend verification email"}
            </button>

            {resendMsg && (
              <p style={{ marginTop: 12, color: "var(--text-color)" }}>
                {resendMsg}
              </p>
            )}

            <button
              onClick={() => {
                setShowResend(false);
                setResendMsg("");
                setCooldown(0);
                if (resendIntervalRef.current) {
                  clearInterval(resendIntervalRef.current);
                  resendIntervalRef.current = null;
                }
              }}
              style={styles.cancelButton}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* 2FA Modal */}
      {show2FAModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Two-Factor Authentication</h3>
            <p style={styles.modalDescription}>
              Enter the 2FA code generated by your authenticator app.
            </p>
            <input
              id="2fa-input"
              type="text"
              placeholder="2FA Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              onKeyDown={handleKeyDown}
              style={styles.input}
            />
            <button onClick={handle2FASubmit} style={styles.button}>
              Submit
            </button>
            <button
              onClick={() => setShow2FAModal(false)}
              style={styles.cancelButton}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: "var(--background-color)",
  },
  formContainer: {
    backgroundColor: "var(--card-background)",
    padding: "40px 20px",
    borderRadius: "16px",
    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.1)",
    width: "100%",
    maxWidth: "400px",
    textAlign: "center",
  },
  title: {
    color: "var(--text-color)",
    marginBottom: "20px",
    fontWeight: "bold",
  },
  input: {
    padding: "12px",
    width: "100%",
    margin: "10px 0",
    color: "var(--text-color)",
    borderRadius: "6px",
    border: "1px solid #ccc",
    fontSize: "16px",
    boxSizing: "border-box",
    backgroundColor: "var(--input-color)",
  },
  button: {
    padding: "12px 20px",
    backgroundColor: "var(--button-background)",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    width: "100%",
    marginTop: "10px",
    fontSize: "16px",
    transition: "background-color 0.3s ease",
  },
  cancelButton: {
    padding: "12px 20px",
    backgroundColor: "#ff4d4d",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    width: "100%",
    marginTop: "10px",
    fontSize: "16px",
    transition: "background-color 0.3s ease",
  },
  errorMessage: {
    color: "red",
    marginBottom: "10px",
  },
  linkText: {
    marginTop: "20px",
    fontSize: "14px",
    color: "var(--text-color)",
  },
  link: {
    color: "var(--link-color)",
    textDecoration: "none",
    fontWeight: "bold",
    transition: "color 0.3s ease",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    overflow: "hidden",
    animation: "fadeIn 0.3s ease",
  },
  modal: {
    position: "absolute",
    top: "55%",
    left: "calc(50% - 8px)",
    transform: "translate(-50%, -50%)",
    backgroundColor: "var(--card-background)",
    padding: "30px",
    borderRadius: "10px",
    width: "90%",
    maxWidth: "400px",
    textAlign: "center",
    boxSizing: "border-box",
    animation: "slideUp 0.3s ease",
  },
  modalTitle: {
    fontSize: "20px",
    marginBottom: "10px",
    color: "var(--text-color)",
  },
  modalDescription: {
    marginBottom: "20px",
    color: "var(--text-color-light)",
  },
};

export default Login;
